"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Lock,
  CheckCircle,
  ClipboardCheck,
  BookOpen,
  Clock,
  Award,
  Tag,
  ArrowRight,
  Sparkles,
  MessageCircle,
  ClipboardList,
  ExternalLink,
  FileText,
  Link as LinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import type { Course } from "@/types";
import type { UniversalAssessment } from "@/types/universal-assessment";
import { checkSequentialAccess, type UniversalAssessmentRequirement } from "@/lib/lesson-access";
import { CourseEnrollmentActions } from "./course-enrollment-actions";

interface LessonAccess {
  hasAccess: boolean;
  isCompleted: boolean;
}

interface UniversalAssessmentResultPreview {
  completedAt: string;
  topTraits: Array<{ traitId: string; value: number; label?: string; description?: string }>;
}

interface CourseDetailClientProps {
  course: Course;
  progressMap: Map<string, { completed: boolean; percentWatched: number }>;
  assessmentMap?: Map<string, Map<string, boolean>>;
  universalAssessmentResultsMap?: Map<string, UniversalAssessmentResultPreview>;
  trackProgressMap?: Map<string, string[]>; // trackGroupId -> completed module IDs
  isAuthenticated: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export function CourseDetailClient({
  course,
  progressMap,
  assessmentMap,
  universalAssessmentResultsMap,
  trackProgressMap,
  isAuthenticated,
}: CourseDetailClientProps) {
  const heroImage = course.heroImageUrl || course.thumbnail;
  const [universalAssessmentsMap, setUniversalAssessmentsMap] = useState<
    Map<string, UniversalAssessment>
  >(new Map());
  const [loadingUniversalAssessments, setLoadingUniversalAssessments] = useState(true);
  const discussionBoards = course.discussionBoards ?? [];
  const customForms = course.customForms ?? [];
  const hasDiscussionBoards = discussionBoards.length > 0;
  const hasCustomForms = customForms.length > 0;
  const hasCertificateTemplate =
    typeof course.certificateTemplateId === "string" &&
    course.certificateTemplateId.trim().length > 0 &&
    course.certificateTemplateId !== "none";
  type CourseLesson = Course["modules"][number]["lessons"][number];

  // Fetch universal assessments
  useEffect(() => {
    const fetchUniversalAssessments = async () => {
      try {
        const response = await fetch("/api/universal-assessments");
        if (response.ok) {
          const data = await response.json();
          const assessments: UniversalAssessment[] = data.assessments || [];
          const map = new Map(assessments.map((a) => [a.id, a]));
          setUniversalAssessmentsMap(map);
        }
      } catch (error) {
        console.error("Failed to load universal assessments:", error);
      } finally {
        setLoadingUniversalAssessments(false);
      }
    };

    fetchUniversalAssessments();
  }, []);

  const universalRequirementMap = new Map<string, UniversalAssessmentRequirement[]>();
  course.modules.forEach((module) => {
    const requirements = (module.universalAssessments ?? []).map((assessmentId) => ({
      assessmentId,
      completed: Boolean(universalAssessmentResultsMap?.has(assessmentId)),
    }));
    if (requirements.length > 0) {
      universalRequirementMap.set(module.id, requirements);
    }
  });

  const checkAccess = (lesson: CourseLesson): LessonAccess => {
    if (!isAuthenticated) {
      const isFirstLesson = course.modules[0]?.lessons[0]?.slug === lesson.slug;
      return { hasAccess: isFirstLesson, isCompleted: false };
    }

    const access = checkSequentialAccess(
      course,
      lesson.slug,
      progressMap,
      assessmentMap,
      universalRequirementMap,
      trackProgressMap
    );

    return {
      hasAccess: access.hasAccess,
      isCompleted: access.isCompleted,
    };
  };

  // Build a combined content array of modules and track groups, sorted by order
  type ContentItem =
    | { type: 'module'; data: typeof course.modules[number]; index: number }
    | { type: 'trackGroup'; data: NonNullable<typeof course.trackGroups>[number] };

  const contentItems: ContentItem[] = [];

  // Track which modules are part of track groups to avoid rendering them separately
  const trackModuleIds = new Set<string>();
  course.trackGroups?.forEach((tg) => {
    tg.trackModuleIds.forEach((id) => trackModuleIds.add(id));
  });

  // Add regular modules (excluding track modules)
  course.modules.forEach((module, index) => {
    if (!module.isTrack && !trackModuleIds.has(module.id)) {
      contentItems.push({ type: 'module', data: module, index });
    }
  });

  // Add track groups
  course.trackGroups?.forEach((trackGroup) => {
    contentItems.push({ type: 'trackGroup', data: trackGroup });
  });

  // Sort by order
  contentItems.sort((a, b) => {
    const orderA = a.type === 'module' ? a.data.order : a.data.order;
    const orderB = b.type === 'module' ? b.data.order : b.data.order;
    return orderA - orderB;
  });

  return (
    <div className="bg-gradient-to-b from-secondary/30 via-background to-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border/50 bg-gradient-to-br from-primary/5 via-background to-secondary/20 py-20">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          {heroImage ? (
            <Image
              src={heroImage}
              alt={`${course.title} background`}
              fill
              className="object-cover opacity-30"
              sizes="100vw"
              priority
              unoptimized
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-br from-background/80 via-background/70 to-secondary/30" />
          <div className="absolute -right-20 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -left-20 bottom-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="container mx-auto"
        >
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <motion.div variants={itemVariants} className="flex-1 space-y-6">
              <motion.span
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary backdrop-blur-sm"
              >
                <BookOpen className="h-3 w-3" />
                {course.category} · {course.level}
              </motion.span>

              <div className="space-y-3">
                <h1 className="text-4xl font-bold leading-tight text-foreground sm:text-5xl lg:text-6xl">
                  {course.title}
                </h1>
                <p className="text-xl text-muted-foreground">{course.subtitle}</p>
              </div>

              <motion.div variants={itemVariants} className="flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-2 rounded-lg bg-secondary/80 px-4 py-2 font-medium text-secondary-foreground">
                  <Clock className="h-4 w-4" />
                  {course.estimatedMinutes} min
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg bg-secondary/80 px-4 py-2 font-medium text-secondary-foreground">
                  <BookOpen className="h-4 w-4" />
                  {course.moduleCount} modules
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg bg-secondary/80 px-4 py-2 font-medium text-secondary-foreground">
                  <ClipboardCheck className="h-4 w-4" />
                  {course.lessonCount} lessons
                </span>
              </motion.div>

              <motion.div variants={itemVariants} className="pt-4">
                <CourseEnrollmentActions
                  course={course}
                  progressMap={progressMap}
                  assessmentMap={assessmentMap}
                  universalAssessmentResultsMap={universalAssessmentResultsMap}
                  trackProgressMap={trackProgressMap}
                />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Content Section */}
      <section className="container mx-auto grid gap-12 py-20 lg:grid-cols-[2fr,1fr]">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-12"
        >
          {/* Learning Outcomes */}
          <motion.div variants={itemVariants} className="space-y-6">
            <h2 className="text-3xl font-bold text-foreground">What you will learn</h2>
            <div className="grid gap-3">
              {course.featuredOutcomes.map((outcome, index) => (
                <motion.div
                  key={outcome}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  whileHover={{ x: 4, transition: { duration: 0.2 } }}
                  className="group flex items-start gap-4 rounded-xl border border-border/50 bg-gradient-to-r from-background to-secondary/20 px-5 py-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="mt-0.5 rounded-full bg-primary/20 p-1">
                    <CheckCircle className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm leading-relaxed text-muted-foreground">{outcome}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {hasDiscussionBoards ? (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="flex items-center gap-3">
                <MessageCircle className="h-6 w-6 text-primary" />
                <h2 className="text-3xl font-bold text-foreground">Discussion &amp; Community</h2>
              </div>
              <div className="space-y-4">
                {discussionBoards.map((board) => (
                  <div
                    key={board.id}
                    className="space-y-4 rounded-xl border border-border/60 bg-background/80 p-6 shadow-sm backdrop-blur"
                  >
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="text-lg font-semibold text-foreground">{board.title}</h3>
                          <Badge variant="secondary" className="text-xs uppercase tracking-wide">
                            {formatProviderLabel(board.provider)}
                          </Badge>
                        </div>
                        {board.description ? (
                          <p className="text-sm text-muted-foreground">{board.description}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-start gap-2 md:items-end">
                        <Button asChild variant="outline" className="gap-2">
                          <a href={board.url} target="_blank" rel="noopener noreferrer">
                            <MessageCircle className="h-4 w-4" /> Join conversation
                          </a>
                        </Button>
                        {board.requireLogin ? (
                          <span className="text-xs text-muted-foreground">Sign-in required</span>
                        ) : null}
                      </div>
                    </div>
                    {board.instructions ? (
                      <p className="text-sm text-muted-foreground">{board.instructions}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </motion.div>
          ) : null}

          {hasCustomForms ? (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-6 w-6 text-primary" />
                <h2 className="text-3xl font-bold text-foreground">Next steps &amp; forms</h2>
              </div>
              <div className="space-y-4">
                {customForms.map((form) => (
                  <div
                    key={form.id}
                    className="space-y-4 rounded-xl border border-border/60 bg-background/80 p-6 shadow-sm backdrop-blur"
                  >
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-2 md:max-w-2xl">
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="text-lg font-semibold text-foreground">{form.title}</h3>
                          {form.requiresLogin ? (
                            <Badge variant="outline" className="text-xs uppercase tracking-wide">
                              Login required
                            </Badge>
                          ) : null}
                        </div>
                        {form.description ? (
                          <p className="text-sm text-muted-foreground">{form.description}</p>
                        ) : null}
                        {form.successMessage ? (
                          <p className="text-xs text-muted-foreground">
                            Confirmation message: {form.successMessage}
                          </p>
                        ) : null}
                        {form.notifyEmails && form.notifyEmails.length > 0 ? (
                          <p className="text-xs text-muted-foreground">
                            Notifications sent to {form.notifyEmails.join(", ")}
                          </p>
                        ) : null}
                      </div>
                      {form.url ? (
                        <Button asChild variant="secondary" className="gap-2 self-start">
                          <a href={form.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                            {form.buttonLabel ?? "Open form"}
                          </a>
                        </Button>
                      ) : null}
                    </div>
                    {form.embedHtml ? (
                      <div
                        className="rounded-lg border border-dashed border-border/60 bg-background/70 p-4"
                        dangerouslySetInnerHTML={{ __html: form.embedHtml }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </motion.div>
          ) : null}

          {/* Course Modules and Track Groups */}
          <motion.div variants={itemVariants} className="space-y-6">
            <h2 className="text-3xl font-bold text-foreground">Course content</h2>
            <div className="space-y-6">
              {contentItems.map((item, itemIndex) => {
                if (item.type === 'trackGroup') {
                  const trackGroup = item.data;
                  const completedTrackModuleIds = trackProgressMap?.get(trackGroup.id) || [];
                  const trackModules = course.modules.filter((m) =>
                    trackGroup.trackModuleIds.includes(m.id)
                  );
                  const completedCount = completedTrackModuleIds.length;
                  const hasMetRequirement = completedCount >= trackGroup.requiredTracksCount;

                  return (
                    <motion.article
                      key={`track-group-${trackGroup.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: itemIndex * 0.15, duration: 0.5 }}
                      className="overflow-hidden rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-background to-secondary/10 shadow-lg"
                    >
                      <div className="border-b border-primary/20 bg-gradient-to-r from-primary/10 to-transparent p-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                              <Award className="h-3 w-3" />
                              Track Selection
                            </div>
                            <h3 className="text-xl font-semibold text-foreground">{trackGroup.title}</h3>
                            <p className="mt-1 text-sm text-muted-foreground">{trackGroup.description}</p>
                          </div>
                          {hasMetRequirement && (
                            <Badge variant="default" className="gap-1 self-start sm:self-auto">
                              <CheckCircle className="h-3 w-3" />
                              Requirement met
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="mb-4 flex flex-wrap gap-3 text-sm text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <BookOpen className="h-4 w-4" />
                            {trackModules.length} tracks available
                          </span>
                          <span className="inline-flex items-center gap-1">
                            Progress: {completedCount} / {trackModules.length}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            Required: {trackGroup.requiredTracksCount} track{trackGroup.requiredTracksCount > 1 ? 's' : ''}
                          </span>
                        </div>

                        {trackGroup.instructions && (
                          <p className="mb-4 text-sm text-muted-foreground">{trackGroup.instructions}</p>
                        )}

                        {/* Track preview list */}
                        <div className="mb-4 space-y-2">
                          {trackModules.slice(0, 4).map((trackModule) => {
                            const isCompleted = completedTrackModuleIds.includes(trackModule.id);
                            return (
                              <div
                                key={trackModule.id}
                                className="flex items-center gap-2 rounded-lg border border-border/40 bg-background/50 px-3 py-2 text-sm"
                              >
                                {isCompleted ? (
                                  <CheckCircle className="h-4 w-4 flex-shrink-0 text-success" />
                                ) : (
                                  <div className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-border" />
                                )}
                                {trackModule.trackIcon && (
                                  <span className="text-lg">{trackModule.trackIcon}</span>
                                )}
                                <span className={isCompleted ? "text-muted-foreground" : "font-medium"}>
                                  {trackModule.trackName || trackModule.title}
                                </span>
                              </div>
                            );
                          })}
                          {trackModules.length > 4 && (
                            <p className="text-xs text-muted-foreground">
                              + {trackModules.length - 4} more track{trackModules.length - 4 > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>

                        <Button asChild variant="default" className="w-full gap-2" size="lg">
                          <Link href={`/courses/${course.slug}/tracks/${trackGroup.id}`}>
                            {completedCount > 0 ? 'View Tracks' : 'Choose Track'}
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </motion.article>
                  );
                }

                // Regular module rendering
                const module = item.data;
                const moduleIndex = item.index;
                const moduleLessonsCompleted =
                  module.lessons.length > 0 &&
                  module.lessons.every((lesson) => {
                    const lessonStatus = checkAccess(lesson);
                    return lessonStatus.isCompleted;
                  });

                return (
                  <motion.article
                    key={module.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: itemIndex * 0.15, duration: 0.5 }}
                    className="overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-background to-secondary/20 shadow-lg"
                  >
                    <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-transparent p-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-foreground">{module.title}</h3>
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                          <BookOpen className="h-3 w-3" />
                          {module.lessons.length} lessons
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">{module.summary}</p>
                    </div>

                    <div className="space-y-3 p-6">
                      {module.lessons.map((lesson) => {
                        const typedLesson = lesson as CourseLesson;
                        const lessonAccess = checkAccess(typedLesson);
                        const resources = typedLesson.resources ?? [];
                        const lessonDuration = typedLesson.durationMinutes ?? typedLesson.duration ?? 0;
                        const lessonHref = lessonAccess.hasAccess
                          ? `/courses/${course.slug}/lessons/${typedLesson.slug}`
                          : "#";

                        return (
                          <div
                            key={lesson.id}
                            className="space-y-3 rounded-xl border border-border/50 bg-background p-4"
                          >
                            <Link
                              href={lessonHref}
                              className={`group flex items-center justify-between transition ${
                                lessonAccess.hasAccess
                                  ? "hover:text-primary"
                                  : "cursor-not-allowed text-muted-foreground/70"
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="flex-shrink-0">
                                  {lessonAccess.isCompleted ? (
                                    <div className="rounded-full bg-success/10 p-1">
                                      <CheckCircle className="h-5 w-5 text-success" />
                                    </div>
                                  ) : lessonAccess.hasAccess ? (
                                    <div className="rounded-full border-2 border-primary/30 bg-primary/5 p-1">
                                      <div className="h-3 w-3 rounded-full border-2 border-border" />
                                    </div>
                                  ) : (
                                    <div className="rounded-full bg-muted p-1">
                                      <Lock className="h-5 w-5 text-muted-foreground/50" />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p
                                    className={`font-semibold ${
                                      lessonAccess.hasAccess ? "text-foreground" : "text-muted-foreground/50"
                                    }`}
                                  >
                                    {typedLesson.title}
                                  </p>
                                  <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                                    <Clock className="h-3 w-3" />
                                    {lessonDuration} min · {typedLesson.type}
                                  </p>
                                </div>
                              </div>
                              {lessonAccess.hasAccess && (
                                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                              )}
                            </Link>

                            {lessonAccess.hasAccess && resources.length > 0 ? (
                              <div className="flex flex-wrap gap-2 border-t border-dashed border-border/50 pt-3">
                                {resources.map((resource) => {
                                  const isDownload = resource.download ?? resource.type === "file";
                                  const openInNewTab = resource.openInNewTab ?? resource.type === "link";
                                  const icon =
                                    resource.type === "file" || resource.type === "worksheet"
                                      ? <FileText className="h-4 w-4" />
                                      : <LinkIcon className="h-4 w-4" />;

                                  return (
                                    <Button
                                      key={resource.id}
                                      asChild
                                      variant="outline"
                                      size="sm"
                                      className="gap-2"
                                    >
                                      <a
                                        href={resource.url}
                                        target={openInNewTab ? "_blank" : "_self"}
                                        rel={openInNewTab ? "noopener noreferrer" : undefined}
                                        download={isDownload ? resource.label || true : undefined}
                                      >
                                        {icon}
                                        <span>{resource.label}</span>
                                      </a>
                                    </Button>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>

                    {module.assessments && module.assessments.length > 0 && (
                      <div className="mx-6 mb-6 space-y-4 rounded-xl border border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5">
                        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                          <ClipboardCheck className="h-5 w-5" />
                          Module assessment{module.assessments.length > 1 ? "s" : ""}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {moduleLessonsCompleted
                            ? "Take the assessment to confirm mastery of this module."
                            : "Complete the lessons above to unlock this assessment."}
                        </p>
                        <div className="space-y-3">
                          {module.assessments.map((assessment) => {
                            const assessmentSlug = assessment.slug || assessment.id;
                            const moduleAssessments = assessmentMap?.get(module.id);
                            const assessmentPassed = moduleAssessments?.get(assessment.id);

                            return (
                              <Link
                                key={assessment.id}
                                href={
                                  moduleLessonsCompleted
                                    ? `/courses/${course.slug}/assessments/${assessmentSlug}`
                                    : "#"
                                }
                                className={`flex flex-col gap-2 rounded-lg border p-4 transition ${
                                  moduleLessonsCompleted
                                    ? "border-primary/40 bg-background hover:border-primary/60 hover:shadow"
                                    : "cursor-not-allowed border-border/40 bg-muted/20 text-muted-foreground"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-foreground">
                                      {assessment.title}
                                    </span>
                                    {assessment.required && (
                                      <Badge variant="destructive" className="text-[10px] px-2 py-0">
                                        Required
                                      </Badge>
                                    )}
                                    {assessmentPassed && (
                                      <CheckCircle className="h-4 w-4 text-success" />
                                    )}
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    ~{assessment.estimatedMinutes} min
                                  </span>
                                </div>
                                {assessment.description && (
                                  <p className="text-xs text-muted-foreground/80">
                                    {assessment.description}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground/80">
                                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary/40 px-2 py-0.5">
                                    {assessment.questions.length} question
                                    {assessment.questions.length === 1 ? "" : "s"}
                                  </span>
                                  {assessment.passingScore && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary/40 px-2 py-0.5">
                                      Pass ≥ {assessment.passingScore}%
                                    </span>
                                  )}
                                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary/40 px-2 py-0.5">
                                    Allow retake: {assessment.allowRetake ? "Yes" : "No"}
                                  </span>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {module.universalAssessments && module.universalAssessments.length > 0 && (
                      <div className="mx-6 mb-6 space-y-4 rounded-xl border border-dashed border-emerald-600/30 bg-gradient-to-br from-emerald-500/5 to-transparent p-5">
                        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                          <Sparkles className="h-5 w-5" />
                          Universal assessment{module.universalAssessments.length > 1 ? "s" : ""}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {loadingUniversalAssessments
                            ? "Loading assessments..."
                            : moduleLessonsCompleted
                              ? "These assessments help you discover your unique gifts and strengths. Your results are saved and reused across all courses."
                              : "Complete the lessons above to unlock these assessments."}
                        </p>
                        <div className="space-y-3">
                          {module.universalAssessments.map((assessmentId) => {
                            const assessment = universalAssessmentsMap.get(assessmentId);
                            const userResult = universalAssessmentResultsMap?.get(assessmentId);
                            if (!assessment && !loadingUniversalAssessments) return null;

                            return (
                              <Link
                                key={assessmentId}
                                href={
                                  moduleLessonsCompleted && assessment
                                    ? userResult
                                      ? `/courses/${course.slug}/universal-assessments/${assessment.slug}`
                                      : `/courses/${course.slug}/universal-assessments/${assessment.slug}`
                                    : "#"
                                }
                                className={`flex flex-col gap-3 rounded-lg border p-4 transition ${
                                  moduleLessonsCompleted && assessment
                                    ? "border-emerald-600/40 bg-background hover:border-emerald-600/60 hover:shadow"
                                    : "cursor-not-allowed border-border/40 bg-muted/20 text-muted-foreground"
                                }`}
                              >
                                {loadingUniversalAssessments ? (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                    Loading...
                                  </div>
                                ) : assessment ? (
                                  <>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Sparkles className="h-4 w-4 text-emerald-600" />
                                        <span className="font-semibold text-foreground">
                                          {assessment.title}
                                        </span>
                                        {userResult && (
                                          <CheckCircle className="h-4 w-4 text-success" />
                                        )}
                                      </div>
                                      <span className="text-xs text-muted-foreground">
                                        ~{assessment.estimatedMinutes} min
                                      </span>
                                    </div>
                                    {assessment.subtitle && (
                                      <p className="text-xs text-muted-foreground/80">
                                        {assessment.subtitle}
                                      </p>
                                    )}
                                    
                                    {userResult && userResult.topTraits.length > 0 && (
                                      <div className="space-y-2 border-t border-dashed border-border/50 pt-3">
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs font-medium text-emerald-600">Your Results</span>
                                          <span className="text-[10px] text-muted-foreground">
                                            Completed {new Date(userResult.completedAt).toLocaleDateString()}
                                            {" • "}
                                            {formatTimeAgo(new Date(userResult.completedAt))}
                                          </span>
                                        </div>
                                        <div className="space-y-2">
                                          {userResult.topTraits.map((trait, index) => {
                                            const maxValue = userResult.topTraits[0]?.value || 1;
                                            const percentage = Math.round((trait.value / maxValue) * 100);
                                            
                                            return (
                                              <div key={trait.traitId} className="space-y-1">
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-2">
                                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                                      #{index + 1}
                                                    </Badge>
                                                    <span className="text-xs font-medium text-foreground">
                                                      {trait.label || trait.traitId}
                                                    </span>
                                                  </div>
                                                  <span className="text-xs text-muted-foreground">
                                                    {Math.round(trait.value)} pts
                                                  </span>
                                                </div>
                                                <Progress value={percentage} className="h-1.5" />
                                              </div>
                                            );
                                          })}
                                        </div>
                                        <p className="text-[10px] text-muted-foreground/70 italic">
                                          Click to view full results or retake
                                        </p>
                                      </div>
                                    )}

                                    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground/80">
                                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/10 px-2 py-0.5 text-emerald-600">
                                        Reusable across courses
                                      </span>
                                      {assessment.allowRetake && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/40 px-2 py-0.5">
                                          Retake allowed
                                        </span>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-sm text-muted-foreground">
                                    Assessment not found
                                  </div>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </motion.article>
                );
              })}
            </div>
          </motion.div>
        </motion.div>

        {/* Sidebar */}
        <motion.aside
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="space-y-6"
        >
          {hasCertificateTemplate && (
            <motion.div
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-background to-secondary/20 p-6 shadow-lg"
            >
              <div className="mb-3 flex items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Certificate</h3>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                Earn a certificate upon completing this course.
              </p>
              <Link
                href={`/courses/${course.slug}/certificate/preview`}
                className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-medium text-primary transition-all hover:bg-primary/20 hover:shadow-md"
              >
                Preview certificate
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          )}

          {course.tags.length > 0 && (
            <motion.div
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-background to-secondary/20 p-6 shadow-lg"
            >
              <div className="mb-3 flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Tags</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {course.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-border/50 bg-background/50 px-3 py-1 text-xs text-muted-foreground"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </motion.div>
          )}
        </motion.aside>
      </section>
    </div>
  );
}

function formatProviderLabel(provider: string): string {
  switch (provider.toLowerCase()) {
    case "discord":
      return "Discord";
    case "slack":
      return "Slack";
    case "circle":
      return "Circle";
    case "discourse":
      return "Discourse";
    case "facebook":
      return "Facebook Group";
    default:
      return provider;
  }
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffMins < 60) {
    return diffMins <= 1 ? "just now" : `${diffMins} mins ago`;
  } else if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  } else if (diffDays < 7) {
    return diffDays === 1 ? "yesterday" : `${diffDays} days ago`;
  } else if (diffWeeks < 4) {
    return diffWeeks === 1 ? "1 week ago" : `${diffWeeks} weeks ago`;
  } else if (diffMonths < 12) {
    return diffMonths === 1 ? "1 month ago" : `${diffMonths} months ago`;
  } else {
    return diffYears === 1 ? "1 year ago" : `${diffYears} years ago`;
  }
}
